import { useCallback, useMemo } from 'react'

import { PageHeader } from 'components/PageHeader'
import { useOrgContext } from 'context/OrgContext'
import { useUserContext } from 'context/UserContext'
import { signOut } from 'features/Auth/authService'
import { recoverAccessToken } from 'features/Auth/sessionRefresh'
import { useNotifyAsyncExportStarted } from 'features/Reports/api/useNotifyAsyncExportStarted'
import useFeatureFlags from 'hooks/useFeatureFlags'

type RocketUser = Omit<
    NonNullable<ReturnType<typeof useUserContext>['currentUser']>,
    'id'
> & { id: number }

type RocketModuleContext = ReturnType<typeof useOrgContext> & {
    userId: number | undefined
    user: RocketUser | undefined
    onAsyncExportStarted: ReturnType<typeof useNotifyAsyncExportStarted>
}

// The contract for the props Forge passes into every Rocket module-federation
// expose. Mirrors Rocket's `RemoteEntryProps` (defined in the Rocket repo, so
// the two can't share the type — keep them in sync). Typing this explicitly,
// rather than `Record<string, unknown>`, lets TypeScript flag drift between the
// `/ai` route mount and the floating-chat mount.
export type RocketComponentProps = {
    featureFlags: ReturnType<typeof useFeatureFlags>
    hostAppId: string
    orgContext: RocketModuleContext
    moduleContext: RocketModuleContext
    pageHeaderComponent: typeof PageHeader
    onUnauthenticated: () => void
    // Rocket shares Forge's stored tokens, so it refreshes through Forge's
    // refresher rather than racing it for the same rotating refresh token.
    refreshSession: typeof recoverAccessToken
}

// Props passed to every Rocket module-federation expose (the `/ai` route and the
// app-wide floating chat popup). Kept in one hook so the two mount sites can't
// drift apart.
export function useRocketComponentProps(): RocketComponentProps {
    const featureFlags = useFeatureFlags()
    const orgContext = useOrgContext()
    const { currentUser } = useUserContext()
    const notifyAsyncExportStarted = useNotifyAsyncExportStarted()

    const handleRocketUnauthenticated = useCallback(() => {
        void signOut()
    }, [])

    const rocketModuleContext = useMemo(() => {
        const user = currentUser?.id
            ? { ...currentUser, id: Number(currentUser.id) }
            : undefined
        return {
            ...orgContext,
            userId: user?.id,
            user,
            onAsyncExportStarted: notifyAsyncExportStarted,
        }
    }, [orgContext, currentUser, notifyAsyncExportStarted])

    return useMemo(
        () => ({
            featureFlags,
            hostAppId: 'forge',
            orgContext: rocketModuleContext,
            moduleContext: rocketModuleContext,
            pageHeaderComponent: PageHeader,
            onUnauthenticated: handleRocketUnauthenticated,
            refreshSession: recoverAccessToken,
        }),
        [rocketModuleContext, featureFlags, handleRocketUnauthenticated]
    )
}
